import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';

interface DbSyncLogsState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;
    retryingLogId: number | null;
    documents: any[];
    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
    };
}

const state: DbSyncLogsState = {
    loading: false,
    loaded: false,
    fatalError: false,
    retryingLogId: null,
    documents: [],
    lastMeta: {
        page: 1,
        pageSize: 20,
        totalCount: 0
    }
};

export const LOGS_LIST = 'DB_SYNC_LOGS_LIST';
export const RETRY_SYNC = 'DB_SYNC_RETRY';

export const actions: ActionTree<DbSyncLogsState, RootState> = {
    loadLogs({ commit }, { configId, page }) {
        const vm: any = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(LOGS_LIST)
                    .raw('db_sync.logs_list')
                    .action({
                        config_id: configId,
                        page: page || 1,
                        page_size: 20
                    })
            ])
                .then((result: ServerResult) => {
                    commit('SET_LOGS', result.getOperation(LOGS_LIST).getContent());
                    commit('LOADED_SUCCESS');
                    resolve(null);
                })
                .catch(() => {
                    commit('LOADED_ERROR');
                    reject();
                });
        });
    },

    retryLog({ commit, dispatch }, { logId, configId }) {
        const vm: any = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('SET_RETRYING', logId);

            vm.$db([
                vm.$q(RETRY_SYNC)
                    .raw('db_sync.retry')
                    .action({ log_id: logId })
            ])
                .then((result: ServerResult) => {
                    const op = result.getOperation(RETRY_SYNC);
                    commit('SET_RETRYING', null);

                    if (op.hasError()) {
                        const content = op.getContent() || {};
                        vm.$buefy.toast.open({
                            message: content.text || 'Ошибка повтора',
                            type: 'is-danger'
                        });
                        reject();
                        return;
                    }

                    vm.$buefy.toast.open({
                        message: 'Повтор выполнен',
                        type: 'is-success'
                    });
                    dispatch('loadLogs', { configId, page: 1 });
                    resolve(op.getContent());
                })
                .catch((err: any) => {
                    commit('SET_RETRYING', null);
                    vm.$buefy.toast.open({
                        message: err?.message || 'Ошибка повтора',
                        type: 'is-danger'
                    });
                    reject(err);
                });
        });
    }
};

export const mutations: MutationTree<DbSyncLogsState> = {
    START_LOADING(state) {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS(state) {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR(state) {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_RETRYING(state, logId) {
        state.retryingLogId = logId;
    },

    SET_LOGS(state, data) {
        state.documents = data.documents || [];
        const meta = data.meta || {};
        state.lastMeta = {
            page: meta.page || 1,
            pageSize: meta.page_size || 20,
            totalCount: meta.total_count || 0
        };
    }
};

export const STORE_KEY = '$_db_sync_logs';

export const store = {
    namespaced: true,
    state,
    actions,
    mutations
};
